import { QueryObserverResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createEventAlert,
  deleteEventAlert,
  getAdherenceLogs,
  getStepEventAlerts,
  updateEventAlert,
} from './events-client';
import type { AdherenceLog, AdherenceLogEventType, EventAlert } from './events-client.type';

/**
 * Hook for creating new event alerts
 *
 * @returns Object containing:
 * - createEventAlert: Function to create a new event alert
 * - isCreating: Boolean indicating if creation is in progress
 */
const useCreateEventAlert = (): {
  createEventAlert: (eventAlert: Partial<EventAlert>) => Promise<EventAlert>;
  isCreating: boolean;
} => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (eventAlert: Partial<EventAlert>) => createEventAlert(eventAlert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['event-alerts'] });
    },
  });

  return {
    createEventAlert: (eventAlert: Partial<EventAlert>) => mutation.mutateAsync(eventAlert),
    isCreating: mutation.isPending,
  };
};

/**
 * Hook for updating an event alert
 *
 * @returns Object containing:
 * - updateEventAlert: Function to update an event alert
 * - isUpdating: Boolean indicating if update is in progress
 */
const useUpdateEventAlert = (): {
  updateEventAlert: (eventAlert: Partial<EventAlert>) => Promise<EventAlert>;
  isUpdating: boolean;
} => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (eventAlert: Partial<EventAlert>) => updateEventAlert(eventAlert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['event-alerts'] });
    },
  });

  return {
    updateEventAlert: (eventAlert: Partial<EventAlert>) => mutation.mutateAsync(eventAlert),
    isUpdating: mutation.isPending,
  };
};

/**
 * Hook for deleting an event alert
 *
 * @returns Object containing:
 * - deleteEventAlert: Function to delete an event alert
 * - isDeleting: Boolean indicating if deletion is in progress
 */
const useDeleteEventAlert = (): {
  deleteEventAlert: (eventAlertId: string) => Promise<void>;
  isDeleting: boolean;
} => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: deleteEventAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['event-alerts'] });
    },
  });

  return {
    deleteEventAlert: (eventAlertId: string) => mutation.mutateAsync(eventAlertId),
    isDeleting: mutation.isPending,
  };
};

/**
 * Hook to fetch event alerts for a specific step
 *
 * @param {string} stepId - The ID of the step to fetch event alerts for
 * @param {boolean} enabled - Whether to enable the query (defaults to true if stepId is provided)
 * @returns {Object} Object containing:
 *   - data: The event alerts data
 *   - query: The React Query result object
 */
const useStepEventAlerts = (
  stepId: string | undefined,
  enabled = !!stepId,
): {
  data: EventAlert[] | undefined;
  query: QueryObserverResult<EventAlert[]>;
} => {
  const query = useQuery({
    queryKey: ['event-alerts', 'step', stepId],
    queryFn: () => getStepEventAlerts(stepId || ''),
    enabled: enabled && !!stepId,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch adherence logs for a specific organization and event type
 *
 * @param {string} organizationId - The ID of the organization to fetch adherence logs for
 * @param {AdherenceLogEventType[]} eventTypes - The types of events to fetch adherence logs for
 * @returns {Object} Object containing:
 *   - data: The adherence logs data
 *   - query: The React Query result object
 */
const useAdherenceLogs = (
  organizationId: string | undefined,
  eventTypes?: AdherenceLogEventType[],
): {
  data: AdherenceLog[] | undefined;
  query: QueryObserverResult<AdherenceLog[]>;
} => {
  const query = useQuery({
    queryKey: ['adherence-logs', organizationId, eventTypes],
    queryFn: () => getAdherenceLogs(organizationId || '', eventTypes),
    enabled: !!organizationId && !!eventTypes && eventTypes.length > 0,
  });

  return { data: query.data, query };
};
export { useAdherenceLogs, useCreateEventAlert, useDeleteEventAlert, useStepEventAlerts, useUpdateEventAlert };
